import { forms } from "./plugins/forms";
import { SidebarNav } from "./plugins/sidebar-nav";
import { Subnavigation } from "./plugins/subnavigation";
import { FileUploadManager } from "./plugins/file-upload";

/**
 * Subpage TypeScript Module
 * =========================
 *
 * Handles subpage-specific functionality including enhanced form handling and phone formatting.
 * Core plugins (tabs, accordions, etc.) are handled by atlas.ts which runs on all pages.
 *
 * @version 1.0.0
 */

let formsPlugin: forms;
let subnavigationPlugin: Subnavigation;

let fileUploadManager: FileUploadManager;
/**
 * Manages subpage-specific functionality including enhanced form handling, phone formatting, and content management.
 *
 * This class provides specialized functionality for individual pages that goes beyond the core atlas.ts features.
 * It handles form enhancements, automatic phone number formatting with clickable tel links, empty content
 * section management, and provides backward compatibility with existing ASP.NET partial postback systems.
 *
 * @remarks
 * - Automatically initializes when DOM content is loaded
 * - Integrates with forms plugin for enhanced form field management
 * - Converts phone numbers in structured data to clickable tel links
 * - Hides empty content sections to improve page layout
 * - Maintains global exposure for ASP.NET WebForms compatibility
 * - Provides refresh capabilities for dynamic content updates
 *
 * @example
 * // Automatic initialization (happens on DOMContentLoaded):
 * // const manager = new SubpageManager();
 *
 * // Manual usage after dynamic content changes:
 * if (window.subpageManager) {
 *   window.subpageManager.refresh();
 * }
 *
 * // Get plugin instances:
 * const formsInstance = window.subpageManager.getPlugin('forms');
 *
 * // ASP.NET compatibility:
 * function pageLoad() {
 *   window.pageLoad(); // Calls refreshFormLabels internally
 * }
 *
 * @public
 */
class SubpageManager {

    /**
     * Creates a new SubpageManager instance and initializes all subpage functionality.
     *
     * @remarks Automatically sets up plugins, phone formatting, and content management
     */
    constructor() {
      this.initialize();
    }

    /**
     * Initializes all subpage functionality in the correct order.
     *
     * @remarks Calls setupPlugins, setupPhoneFormatting, and hideEmptyContentSections sequentially
     * @private
     */
    private initialize(): void {
      this.setupPlugins();
      this.setupPhoneFormatting();
      this.hideEmptyContentSections();
    }

  /**
   * Initializes subpage-specific plugins with error handling.
   *
   * @remarks Currently sets up the forms plugin with custom configuration for subpage form enhancement
   * @private
   */
  private setupPlugins(): void {
    try {
      // Initialize forms plugin for subpage-specific form handling
      formsPlugin = new forms({
        focusClass: "focused",
        fileInputSelector: 'input[type="file"]',
      });

      // Initialize subnavigation plugin
      subnavigationPlugin = new Subnavigation();

      // Initialize file upload manager
      fileUploadManager = new FileUploadManager();
    } catch (error) {
      console.error("Error initializing subpage plugins:", error);
    }
  }

  /**
   * Converts phone numbers in structured data elements to clickable tel links with proper formatting.
   *
   * @remarks Targets elements with itemprop="telephone" containing span elements, formats 10-digit US phone numbers as XXX-XXX-XXXX
   * @private
   */
  private setupPhoneFormatting(): void {
    const phoneSpans = document.querySelectorAll(
      '[itemprop="telephone"] > span',
    );

    phoneSpans.forEach((span: Element) => {
      const element = span as HTMLElement;
      const text = element.textContent || "";
      const digits = text.replace(/\D+/g, ""); // Remove non-digit characters

      if (digits.length === 10) {
        const formattedNumber = digits.replace(
          /(\d{3})(\d{3})(\d{4})/,
          "$1-$2-$3",
        );
        element.innerHTML = `<a href="tel:${formattedNumber}">${formattedNumber}</a>`;
      }
    });
  }

  /**
   * Automatically hides content sections that contain no visible text content.
   *
   * @remarks Improves page layout by hiding empty .content-section elements using display: none
   * @private
   */
  private hideEmptyContentSections(): void {
    const contentSections = document.querySelectorAll(".content-section");

    contentSections.forEach((section: Element) => {
      const element = section as HTMLElement;
      const textContent = element.textContent?.trim() || "";

      if (textContent.length === 0) {
        element.style.display = "none";
      }
    });
  }

  /**
   * Refreshes form label states and focus management.
   *
   * @remarks Maintained for backward compatibility with existing ASP.NET partial postback systems
   * @public
   */
  public refreshFormLabels(): void {
    if (formsPlugin) {
      formsPlugin.refresh();
    }
  }

  /**
   * Refreshes all subpage functionality after dynamic content changes.
   *
   * @remarks Useful when content is dynamically loaded via AJAX or other mechanisms. Re-runs phone formatting, content hiding, and plugin refresh
   * @public
   */
  public refresh(): void {
    this.setupPhoneFormatting();
    this.hideEmptyContentSections();

    // Refresh forms plugin if it supports it
    if (formsPlugin && typeof (formsPlugin as any).refresh === "function") {
      (formsPlugin as any).refresh();
    }
  }

    /**
     * Retrieves a plugin instance by name for external access.
     *
     * @param pluginName - Name of the plugin to retrieve (currently supports: 'forms')
     * @returns The requested plugin instance or null if not found
     * @public
     */
    public getPlugin(pluginName: string): any {
      const plugins: { [key: string]: any } = {
        forms: formsPlugin
      };

      return plugins[pluginName] || null;
    }
  }


/**
 * Global instance for backward compatibility with existing ASP.NET WebForms integration.
 *
 * @remarks Exposed as window.subpageManager for external access
 */
let subpageManager: SubpageManager | null = null;

/**
 * Global function for backward compatibility with ASP.NET partial postbacks.
 *
 * @remarks Called automatically by ASP.NET WebForms after partial postbacks to refresh form states
 * @public
 */
function pageLoad(): void {
  if (subpageManager) {
    subpageManager.refreshFormLabels();
  }
}

/**
 * Automatic initialization when DOM content is loaded.
 *
 * Creates the global SubpageManager instance and exposes necessary functions
 * for ASP.NET WebForms compatibility.
 */
document.addEventListener("DOMContentLoaded", () => {
  subpageManager = new SubpageManager();

  // Make functions globally available for backward compatibility
  (window as any).pageLoad = pageLoad;
  (window as any).subpageManager = subpageManager;
});